import { Table as AntTable } from 'antd'
import { Switch } from '@/components/ui'
import type { TableColumnsType, TableProps as AntTableProps } from 'antd'
import {
  AdminTableActions,
  type AdminTableActionsProps,
} from './AdminTableActions'

export interface TableDataItem {
  key: string
  [key: string]: any
}

export interface AdminTableColumn<T = any> {
  title: string
  dataIndex?: string
  width?: number | string
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
  type?: 'text' | 'switch' | 'actions'
  render?: (value: any, record: T, index: number) => React.ReactNode
  onChange?: (key: string, value: any, record: T) => void
  // For actions column
  actions?: Omit<AdminTableActionsProps<T>, 'record'>
}

interface AdminTableProps<T extends TableDataItem = TableDataItem> {
  columns: AdminTableColumn<T>[]
  dataSource: T[]
  loading?: boolean
  pagination?: false | AntTableProps<T>['pagination']
  bordered?: boolean
  className?: string
  rowClassName?: string
  rowKey?: string | ((record: T) => string)
  onChange?: (updatedData: T[]) => void
}

export function AdminTable<T extends TableDataItem = TableDataItem>({
  columns,
  dataSource,
  loading = false,
  pagination = false,
  bordered = true,
  className = '',
  rowClassName = '',
  rowKey = 'key',
  onChange,
}: AdminTableProps<T>) {
  const handleDataChange = (key: string, field: string, value: any) => {
    if (onChange) {
      const updatedData = dataSource.map((item) =>
        item.key === key ? { ...item, [field]: value } : item
      )
      onChange(updatedData)
    }
  }

  const transformedColumns: TableColumnsType<T> = columns.map((col) => {
    const dataIndex = col.dataIndex || col.title

    const baseColumn: any = {
      title: col.title,
      dataIndex: dataIndex,
      key: dataIndex,
      width: col.width,
      align: col.align || 'left',
      sorter: col.sortable
        ? (a: T, b: T) => {
            const aVal = a[dataIndex]
            const bVal = b[dataIndex]

            if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
              return aVal === bVal ? 0 : aVal ? -1 : 1
            }

            if (typeof aVal === 'string' && typeof bVal === 'string') {
              return aVal.localeCompare(bVal)
            }

            if (typeof aVal === 'number' && typeof bVal === 'number') {
              return aVal - bVal
            }

            return 0
          }
        : undefined,
    }

    // Handle different column types
    if (col.type === 'switch') {
      baseColumn.render = (value: boolean, record: T) => (
        <Switch
          checked={value}
          onChange={(checked) => {
            if (col.onChange) {
              col.onChange(record.key, checked, record)
            } else {
              handleDataChange(record.key, dataIndex, checked)
            }
          }}
        />
      )
    } else if (col.type === 'actions') {
      baseColumn.render = (_: any, record: T) => (
        <AdminTableActions<T>
          record={record}
          onEdit={
            col.onChange
              ? (record) => col.onChange?.(record.key, record, record)
              : undefined
          }
          {...col.actions}
        />
      )
    } else if (col.render) {
      baseColumn.render = col.render
    }

    return baseColumn
  })

  return (
    <AntTable<T>
      columns={transformedColumns}
      dataSource={dataSource}
      loading={loading}
      pagination={pagination}
      bordered={bordered}
      className={className}
      rowClassName={rowClassName}
      rowKey={rowKey}
    />
  )
}
